import axios from 'axios';
import { linkChooserFileLink } from './LinkChooserFileLink';
import { endpoints, getFormForLinkType } from './forms/LinkchooserFormBuilder';
import { LinkchooserLinkType } from '../../../../../../../types/api/LinkchooserLinkType';
import { InputType } from '../../../../InputTypes';
import { isMailProtocol, isTelProtocol } from '../../../../../../atoms/Hyperlink';

/*
 * Lookup if a selected element is part of a link.
 * If that is the case return the link node or else the passed selected node.
 * */
const linkLookup = (node) => {
  let tmpNode = node;
  /* Stop look up when reaching paragraph.
       We do not expect a link around a paragraph only within it, around other elements.
       Prevents as well iterating complete dom. */
  while (tmpNode.parentNode && tmpNode.nodeName !== 'P') {
    if (tmpNode.nodeName === 'A') {
      // return found link
      return tmpNode;
    }

    tmpNode = tmpNode.parentNode;
  }

  // no link found, return selected node
  return node;
};

/**
 * Extracts text from current selection
 * @param selection Tiny Selection object
 */
export const getText = (selection) => {
  let content = selection.getContent({ format: 'text' }) || '';
  const selectionNode = linkLookup(selection.getNode());

  if (selectionNode.nodeName === 'A') {
    // if courser inside of link without selection select link
    selection.select(selectionNode);

    if (content === '') {
      content = selectionNode.innerText;
    }

    if (selectionNode.getAttribute('data-link-type') === LinkchooserLinkType.Attachment) {
      content = content.substring(0, content.indexOf(' ('));
    }
  }

  return content;
};

const getDataAnchorAttribute = (selection) => {
  const selectionNode = linkLookup(selection.getNode());

  if (selectionNode.nodeName === 'A') {
    return selectionNode.getAttribute('data-anchor');
  }

  return undefined;
};

/**
 * Extracts resource reference from current selection
 * @param selection Tiny Selection object
 */
export const getResourceReference = (selection) => {
  if (selection) {
    const selectionNode = linkLookup(selection.getNode());

    if (selectionNode.nodeName === 'A') {
      return selectionNode.getAttribute('data-resource-reference');
    }
  }
  return null;
};

/**
 * Extracts href from current selection
 * (fixes a part of Bug SON-6620)
 * @param selection Tiny Selection object
 */
const getHref = (selection) => {
  const selectionNode = linkLookup(selection.getNode());

  if (selectionNode.nodeName === 'A') {
    selection.select(selectionNode);
    return selectionNode.getAttribute('href');
  }
  return null;
};

const isExternalLink = (parameter) => {
  return parameter.q.type === InputType.TEXT;
};

const isFileLink = (parameter) => {
  if (!parameter.q.selected) {
    return false;
  }

  return parameter.q.selected.attachment;
};

const createHrefFromFormModel = (parameter) => {
  const link = parameter.q.type === InputType.TEXT ? parameter.q.value : undefined;

  return link || undefined;
};

function getAnchorFromInput(parameter) {
  return parameter.anchor && parameter.anchor.value !== '' ? parameter.anchor.value : undefined;
}

function getHrefFromInput(parameter) {
  return parameter.q.type === InputType.COMBOBOX && parameter.q.selected
    ? parameter.q.selected.resourceReference
    : undefined;
}

export function createLinkAttributes(parameter) {
  const linkAttributes = {
    href: createHrefFromFormModel(parameter) || '#',
    'data-resource-reference': getHrefFromInput(parameter),
    'data-anchor': getAnchorFromInput(parameter),
    'data-link-type': parameter.linkType.value,
  };

  if (isExternalLink(parameter)) {
    linkAttributes.rel = 'external';
    linkAttributes.target = '_blank';
  }

  // tel and mail open an application and don't need target="_blank"
  if (isTelProtocol(parameter.q?.value) || isMailProtocol(parameter.q?.value)) {
    linkAttributes.target = '';
  }

  if (isFileLink(parameter)) {
    linkAttributes.download = `${parameter.q.selected.title}.${parameter.q.selected.fileExtension}`;
    linkAttributes.type = parameter.q.selected.contentType;
    linkAttributes['data-file-size'] = parameter.q.selected.fileSize;
    linkAttributes['data-link-type'] = LinkchooserLinkType.Attachment;
  }

  return linkAttributes;
}

export const applyToEditor = (parameter, editor) => {
  if (isFileLink(parameter)) {
    editor.selection.setContent(
      linkChooserFileLink(parameter.text.value, parameter.q.selected.fileExtension, createLinkAttributes(parameter)),
    );
  } else if (parameter.text.valueChanged() === true) {
    editor.selection.setContent(editor.dom.createHTML('a', createLinkAttributes(parameter), parameter.text.value));
  } else {
    const currentlySelectedNode = editor.selection.getNode();
    if (currentlySelectedNode.nodeName === 'A') {
      while (currentlySelectedNode.attributes.length > 0) {
        currentlySelectedNode.removeAttribute(currentlySelectedNode.attributes[0].name);
      }
    }

    /* Use formatter if no text changes occur to avoid broken html when selection passes html tag borders. */
    editor.formatter.apply('link', createLinkAttributes(parameter));
  }

  editor.undoManager.add();
};

export const getLinkTypeForCurrentSelection = (selection, defaultLinkType = LinkchooserLinkType.Page) => {
  const selectionNode = linkLookup(selection?.getNode());
  if (selectionNode?.nodeName === 'A') {
    const linkType =
      selectionNode.getAttribute('data-link-type') ||
      // still needed for backwards compatibility for older data with links
      selectionNode.getAttribute('data-breed') ||
      /*
       * Changed to external to address issue of copied links from any website outside of son.
       * Tackles also already existing Links that do not have data-link-type in already existing data of customers.
       * Compare Bug SON-11190
       */
      LinkchooserLinkType.External;

    // map Attachment to Page as they are in the same tab which is identified by id LinkType.Page
    return linkType === LinkchooserLinkType.Attachment ? LinkchooserLinkType.Page : linkType;
  }
  return defaultLinkType;
};

export const getResourceByReference = (resourceReference) => {
  return axios.get(endpoints.RESOURCE, {
    params: {
      reference: resourceReference,
    },
  });
};

export const createTab = (linkType, text, link, anchor) => {
  return {
    linkType,
    form: getFormForLinkType(linkType, text, link, anchor),
  };
};

/*
 * This function needs to be a closure to be able to add links in IE11.
 * Without it the tiny mce editor instance gets into a corrupt state. The mechanic of closures
 * guarantees that we work on a editor instance that is still functional.
 * (fixes a part of Bug SON-6620)
 * */
export const createTabs = (editor, callback) => {
  const resourceReference = getResourceReference(editor.selection);
  const href = getHref(editor.selection);
  const text = getText(editor.selection);
  const anchor = getDataAnchorAttribute(editor.selection);

  if (resourceReference) {
    /**
     * Switched from async await to then syntax because use effect expects a pure function as return value.
     * With async and the closure we need useEffect did not receive a function an threw "func.apply is not a function"
     */
    getResourceByReference(resourceReference)
      .then((resource) => {
        callback([
          createTab(LinkchooserLinkType.Page, text, resource?.data?.content?.topResults[0], anchor),
          createTab(LinkchooserLinkType.Person, text, resource.data?.user?.topResults[0], undefined),
          createTab(LinkchooserLinkType.External, text, href, undefined),
        ]);
      })
      .catch(({ response }) => {
        callback([
          createTab(LinkchooserLinkType.Page, text, { error: response }, undefined),
          createTab(LinkchooserLinkType.Person, text, { error: response }, undefined),
          createTab(LinkchooserLinkType.External, text, href, undefined),
        ]);
      });
  } else {
    callback([
      createTab(LinkchooserLinkType.Page, text, undefined, anchor),
      createTab(LinkchooserLinkType.Person, text, undefined, undefined),
      createTab(LinkchooserLinkType.External, text, href, undefined),
    ]);
  }
};

function getAnchor(element) {
  const node = linkLookup(element);

  return node.nodeName === 'A' ? node : null;
}

export function unlink(editor) {
  editor.undoManager.transact(function () {
    const node = getAnchor(editor.selection.getNode());

    if (node) {
      editor.selection.select(node);
      editor.selection.setContent(node.innerText.replace(/\s?\(.*?\)/gm, ''));
      editor.focus();
    }
  });
}

function onNodeChange(buttonApi) {
  return (event) => {
    buttonApi.setEnabled(getAnchor(event.element)?.nodeName === 'A');
  };
}

export function onSetupLink(editor) {
  return (buttonApi) => {
    editor.on('NodeChange', onNodeChange(buttonApi));
    buttonApi.setEnabled(editor?.selection?.getNode()?.nodeName === 'A');
    return () => {};
  };
}
